/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  ESCENARIOS_CAPTACION,
  FRECUENCIA_MENSUAL,
  FRECUENCIA_SEMANAL,
  FREC_REF_ALC,
  FREC_REF_NO_ALC,
  IPSOS_ALCOHOLICAS,
  IPSOS_NO_ALCOHOLICAS,
  SEMANAS_MES,
  TICKETS_ALCOHOLICAS,
  TICKETS_NO_ALCOHOLICAS,
  TICKET_REF_ALC,
  TICKET_REF_NO_ALC,
} from "../../data/ipsos2025/bebidas";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaBebidas.module.scss";

const TIPOS_ALCOHOLICAS = Object.keys(IPSOS_ALCOHOLICAS.tipoPreferido);
const TIPOS_NO_ALCOHOLICAS = Object.keys(IPSOS_NO_ALCOHOLICAS.tipos);

export const Bebidas = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("alcoholicas");
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  // Simulador alcohólicas
  const [tipoBebidaAlc, setTipoBebidaAlc] = useState("Cerveza");
  const [ticketAlc, setTicketAlc] = useState(TICKET_REF_ALC.valor);
  const [frecuenciaAlc, setFrecuenciaAlc] = useState(FREC_REF_ALC.valor);
  const [captacionAlc, setCaptacionAlc] = useState(4);

  // Simulador no alcohólicas
  const [tipoBebidaNoAlc, setTipoBebidaNoAlc] = useState("Aguas");
  const [ticketNoAlc, setTicketNoAlc] = useState(TICKET_REF_NO_ALC.valor);
  const [frecuenciaNoAlc, setFrecuenciaNoAlc] = useState(FREC_REF_NO_ALC.valor);
  const [captacionNoAlc, setCaptacionNoAlc] = useState(4);

  // =====================================================
  // CÁLCULOS ALCOHÓLICAS
  // =====================================================
  const resultadosAlc = useMemo(() => {
    if (!totalEjecutivos) return null;

    const consume = Math.round(totalEjecutivos * IPSOS_ALCOHOLICAS.consume.valor);
    const noConsume = Math.round(totalEjecutivos * IPSOS_ALCOHOLICAS.noConsume.valor);

    const porTipo = {};
    Object.entries(IPSOS_ALCOHOLICAS.tipoPreferido).forEach(([tipo, dato]) => {
      porTipo[tipo] = { dato, share: dato.valor, personas: Math.round(consume * dato.valor) };
    });

    const porMarca = {};
    Object.entries(IPSOS_ALCOHOLICAS.marcas).forEach(([marca, dato]) => {
      porMarca[marca] = { dato, share: dato.valor, personas: Math.round(consume * dato.valor) };
    });

    const mercadoMensualEstimado = consume * TICKET_REF_ALC.valor * FREC_REF_ALC.valor;

    return {
      consume,
      noConsume,
      porTipo,
      porMarca,
      mercadoMensualEstimado,
      mercadoAnualEstimado: mercadoMensualEstimado * 12,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS NO ALCOHÓLICAS
  // =====================================================
  const resultadosNoAlc = useMemo(() => {
    if (!totalEjecutivos) return null;

    const consumeLightZero = Math.round(totalEjecutivos * IPSOS_NO_ALCOHOLICAS.consumeLightZero.valor);
    const noConsumeLightZero = Math.round(totalEjecutivos * IPSOS_NO_ALCOHOLICAS.noConsumeLightZero.valor);

    const porTipo = {};
    Object.entries(IPSOS_NO_ALCOHOLICAS.tipos).forEach(([tipo, dato]) => {
      porTipo[tipo] = { dato, share: dato.valor, personas: Math.round(totalEjecutivos * dato.valor) };
    });

    const personasAguas = Math.round(totalEjecutivos * IPSOS_NO_ALCOHOLICAS.tipos.Aguas.valor);
    const mercadoSemanalEstimado = personasAguas * TICKET_REF_NO_ALC.valor * FREC_REF_NO_ALC.valor;

    return {
      consumeLightZero,
      noConsumeLightZero,
      porTipo,
      personasAguas,
      mercadoSemanalEstimado,
      mercadoMensualEstimado: mercadoSemanalEstimado * SEMANAS_MES.valor,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD
  // =====================================================
  const calcularOportunidad = useCallback(() => {
    if (vistaActiva === "alcoholicas" && resultadosAlc) {
      const tipoData = resultadosAlc.porTipo[tipoBebidaAlc];
      if (!tipoData) return null;
      const mercadoTotalMensual = tipoData.personas * ticketAlc * frecuenciaAlc;
      const captadoMensual = Math.round(mercadoTotalMensual * (captacionAlc / 100));
      return {
        consumidores: tipoData.personas,
        mercadoTotalMensual,
        captadoMensual,
        captadoAnual: captadoMensual * 12,
      };
    }
    if (vistaActiva === "no-alcoholicas" && resultadosNoAlc) {
      const tipoData = resultadosNoAlc.porTipo[tipoBebidaNoAlc];
      if (!tipoData) return null;
      const mercadoSemanal = tipoData.personas * ticketNoAlc * frecuenciaNoAlc;
      const mercadoTotalMensual = mercadoSemanal * SEMANAS_MES;
      const captadoMensual = Math.round(mercadoTotalMensual * (captacionNoAlc / 100));
      return {
        consumidores: tipoData.personas,
        mercadoTotalMensual,
        captadoMensual,
        captadoAnual: captadoMensual * 12,
      };
    }
    return null;
  }, [
    vistaActiva,
    resultadosAlc,
    resultadosNoAlc,
    tipoBebidaAlc,
    ticketAlc,
    frecuenciaAlc,
    captacionAlc,
    tipoBebidaNoAlc,
    ticketNoAlc,
    frecuenciaNoAlc,
    captacionNoAlc,
  ]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.bebidasPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>BEBIDAS</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.bebidasContent}>
        {/* Tabs */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "alcoholicas" && styles.tabActivo)}
            onClick={() => setVistaActiva("alcoholicas")}
          >
            ALCOHÓLICAS
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "no-alcoholicas" && styles.tabActivo)}
            onClick={() => setVistaActiva("no-alcoholicas")}
          >
            NO ALCOHÓLICAS
          </button>
        </div>

        {/* =====================================================
            VISTA ALCOHÓLICAS
            ===================================================== */}
        {vistaActiva === "alcoholicas" && resultadosAlc && (
          <>
            {/* Resumen */}
            <div className={styles.resumenPenetracion}>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <BadgeDato dato={IPSOS_ALCOHOLICAS.consume}>
                    {(IPSOS_ALCOHOLICAS.consume.valor * 100).toFixed(0)}%
                  </BadgeDato>
                </div>
                <div className={styles.resumenLabel}>Consume bebidas alcohólicas</div>
                <div className={styles.resumenNumero}>{formatNumber(resultadosAlc.consume)} personas</div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>{formatNumber(resultadosAlc.consume)}</span>
                </div>
                <div className={styles.resumenLabel}>Consumidores en edificios</div>
                <div className={styles.resumenDetalle}>{formatNumber(resultadosAlc.noConsume)} no consumen</div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>
                    {formatLocal(convertCLP(resultadosAlc.mercadoMensualEstimado))}
                  </span>
                </div>
                <div className={styles.resumenLabel}>Mercado mensual estimado</div>
                <div className={styles.resumenDetalle}>
                  {formatLocal(convertCLP(resultadosAlc.mercadoAnualEstimado))}/año
                </div>
              </div>
            </div>

            {/* Tabla tipo preferido */}
            <div className={styles.tablaContainer}>
              <h4 className={styles.subtitulo}>Tipo Preferido de Bebida Alcohólica</h4>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thNoUsa)}>No consume</th>
                    <th className={cx("text-center", styles.thSiUsa)}>Sí consume</th>
                    <th className="text-center">Tipo preferido</th>
                    <th className="text-center">% IPSOS</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {TIPOS_ALCOHOLICAS.map((tipo, idx) => {
                    const data = resultadosAlc.porTipo[tipo];
                    return (
                      <tr key={tipo}>
                        {idx === 0 && (
                          <>
                            <td
                              rowSpan={TIPOS_ALCOHOLICAS.length}
                              className={cx("text-center align-middle", styles.celdaNo)}
                            >
                              <div className={styles.celdaContenido}>
                                <BadgeDato dato={IPSOS_ALCOHOLICAS.noConsume}>
                                  {(IPSOS_ALCOHOLICAS.noConsume.valor * 100).toFixed(0)}%
                                </BadgeDato>
                                <div className={styles.valorGrande}>{formatNumber(resultadosAlc.noConsume)}</div>
                              </div>
                            </td>
                            <td
                              rowSpan={TIPOS_ALCOHOLICAS.length}
                              className={cx("text-center align-middle", styles.celdaSi)}
                            >
                              <div className={styles.celdaContenido}>
                                <BadgeDato dato={IPSOS_ALCOHOLICAS.consume}>
                                  {(IPSOS_ALCOHOLICAS.consume.valor * 100).toFixed(0)}%
                                </BadgeDato>
                                <div className={styles.valorGrande}>{formatNumber(resultadosAlc.consume)}</div>
                              </div>
                            </td>
                          </>
                        )}
                        <td className={styles.celdaFrecuencia}>
                          <span className={styles.frecuenciaTexto}>{tipo}</span>
                        </td>
                        <td className="text-center">
                          <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                        </td>
                        <td className="text-center">
                          <strong>{formatNumber(data.personas)}</strong>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Market Share marcas */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Marca preferida</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosAlc.porMarca).map(([marca, data]) => (
                  <div key={marca} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{marca}</div>
                    <div className={styles.cardShare}>
                      <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Consumidores</span>
                        <span className={styles.metricaValor}>{formatNumber(data.personas)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 56, base 400/275):</strong> 68,9% consume bebidas alcohólicas; la cerveza
                lidera con 47,4% como tipo preferido.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> mercado estimado considerando ticket promedio de{" "}
                {formatLocal(convertCLP(TICKET_REF_ALC.valor))} y {FREC_REF_ALC.valor} ocasiones/mes.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA NO ALCOHÓLICAS
            ===================================================== */}
        {vistaActiva === "no-alcoholicas" && resultadosNoAlc && (
          <>
            {/* Resumen */}
            <div className={styles.resumenPenetracion}>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>{formatNumber(totalEjecutivos)}</span>
                </div>
                <div className={styles.resumenLabel}>Total personas</div>
                <div className={styles.resumenDetalle}>en edificios seleccionados</div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <BadgeDato dato={IPSOS_NO_ALCOHOLICAS.consumeLightZero}>
                    {(IPSOS_NO_ALCOHOLICAS.consumeLightZero.valor * 100).toFixed(0)}%
                  </BadgeDato>
                </div>
                <div className={styles.resumenLabel}>Consume Light / Zero</div>
                <div className={styles.resumenNumero}>{formatNumber(resultadosNoAlc.consumeLightZero)} personas</div>
              </div>
              <div className={styles.resumenCard}>
                <div className={styles.resumenValor}>
                  <span className={styles.valorDestacado}>
                    {formatLocal(convertCLP(resultadosNoAlc.mercadoSemanalEstimado))}
                  </span>
                </div>
                <div className={styles.resumenLabel}>Mercado semanal (Aguas)</div>
                <div className={styles.resumenDetalle}>
                  {formatLocal(convertCLP(resultadosNoAlc.mercadoMensualEstimado))}/mes
                </div>
              </div>
            </div>

            {/* Gráfico de barras horizontales por tipo */}
            <div className={styles.tablaContainer}>
              <h4 className={styles.subtitulo}>Consumo por Tipo de Bebida</h4>
              <div className={styles.canalesChart}>
                {Object.entries(IPSOS_NO_ALCOHOLICAS.tipos).map(([tipo, dato]) => {
                  const personas = resultadosNoAlc.porTipo[tipo].personas;
                  return (
                    <div key={tipo} className={styles.canalBarItem}>
                      <div className={styles.canalBarLabel}>
                        <span>{tipo}</span>
                        <span>
                          <BadgeDato dato={dato}>{(dato.valor * 100).toFixed(0)}%</BadgeDato>
                          <span className={styles.canalBarPct} style={{ marginLeft: "0.5rem" }}>
                            {formatNumber(personas)}
                          </span>
                        </span>
                      </div>
                      <div className={styles.canalBarTrack}>
                        <div className={styles.canalBarFill} style={{ width: `${dato.valor * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Destacado Light/Zero */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Light / Zero — Segmentación</h4>
              <div className={styles.marketShareGrid}>
                <div className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>Consume Light / Zero</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={IPSOS_NO_ALCOHOLICAS.consumeLightZero}>
                      {(IPSOS_NO_ALCOHOLICAS.consumeLightZero.valor * 100).toFixed(0)}%
                    </BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Personas</span>
                      <span className={styles.metricaValor}>{formatNumber(resultadosNoAlc.consumeLightZero)}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>No consume Light / Zero</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={IPSOS_NO_ALCOHOLICAS.noConsumeLightZero}>
                      {(IPSOS_NO_ALCOHOLICAS.noConsumeLightZero.valor * 100).toFixed(0)}%
                    </BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Personas</span>
                      <span className={styles.metricaValor}>{formatNumber(resultadosNoAlc.noConsumeLightZero)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            <div className={styles.disclaimer}>
              <p>
                <strong>Ipsos 2025 (lámina 58, base 400):</strong> consumo de bebidas no alcohólicas medido como
                multirespuesta: aguas (71,7%) y bebidas/gaseosas (63,1%) lideran. El 71% consume opciones Light / Zero.
              </p>
              <p>
                <strong>Supuestos comerciales:</strong> mercado semanal estimado usando aguas como base, ticket de{" "}
                {formatLocal(convertCLP(TICKET_REF_NO_ALC.valor))} y {FREC_REF_NO_ALC.valor} compras por semana.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosAlc || resultadosNoAlc) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                Simulador de Oportunidad - Bebidas {vistaActiva === "alcoholicas" ? "Alcohólicas" : "No Alcohólicas"}
              </h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  {vistaActiva === "alcoholicas" ? (
                    <>
                      <div className={styles.controlGroup}>
                        <label>Tipo de bebida:</label>
                        <select
                          value={tipoBebidaAlc}
                          onChange={(e) => setTipoBebidaAlc(e.target.value)}
                          className="form-select form-select-sm"
                        >
                          {TIPOS_ALCOHOLICAS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>Ticket promedio (supuesto):</label>
                        <select
                          value={ticketAlc}
                          onChange={(e) => setTicketAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {TICKETS_ALCOHOLICAS.map((t) => (
                            <option key={t.valor} value={t.valor}>
                              {formatLocal(convertCLP(t.valor))}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>Frecuencia/mes (supuesto):</label>
                        <select
                          value={frecuenciaAlc}
                          onChange={(e) => setFrecuenciaAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {FRECUENCIA_MENSUAL.map((f) => (
                            <option key={f.valor} value={f.valor}>
                              {f.valor} ocasiones
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>% captación mercado (supuesto):</label>
                        <select
                          value={captacionAlc}
                          onChange={(e) => setCaptacionAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {ESCENARIOS_CAPTACION.map((p) => (
                            <option key={p.valor} value={p.valor}>
                              {p.valor}%
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.controlGroup}>
                        <label>Tipo de bebida:</label>
                        <select
                          value={tipoBebidaNoAlc}
                          onChange={(e) => setTipoBebidaNoAlc(e.target.value)}
                          className="form-select form-select-sm"
                        >
                          {TIPOS_NO_ALCOHOLICAS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>Ticket promedio (supuesto):</label>
                        <select
                          value={ticketNoAlc}
                          onChange={(e) => setTicketNoAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {TICKETS_NO_ALCOHOLICAS.map((t) => (
                            <option key={t.valor} value={t.valor}>
                              {formatLocal(convertCLP(t.valor))}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>Frecuencia/semana (supuesto):</label>
                        <select
                          value={frecuenciaNoAlc}
                          onChange={(e) => setFrecuenciaNoAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {FRECUENCIA_SEMANAL.map((f) => (
                            <option key={f.valor} value={f.valor}>
                              {f.valor} {f.valor === 1 ? "compra" : "compras"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className={styles.controlGroup}>
                        <label>% captación mercado (supuesto):</label>
                        <select
                          value={captacionNoAlc}
                          onChange={(e) => setCaptacionNoAlc(Number(e.target.value))}
                          className="form-select form-select-sm"
                        >
                          {ESCENARIOS_CAPTACION.map((p) => (
                            <option key={p.valor} value={p.valor}>
                              {p.valor}%
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad();
                  if (!oportunidad) return null;
                  const captacionPct = vistaActiva === "alcoholicas" ? captacionAlc : captacionNoAlc;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Consumidores del tipo:</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.consumidores)}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual:</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotalMensual))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Captación al {captacionPct}%:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado captado anual:</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captadoAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bebidas;
